import { useState } from "react";
import { Table, Button, Modal, Form, Input, Select, DatePicker, InputNumber, Popconfirm, message, Space, Tag } from "antd";
import { Plus } from "lucide-react";
import dayjs from "dayjs";
import { useDocClients, useTenders, useTenderCreate, useTenderUpdate, useTenderDelete } from "../../../../api-hooks/documents";

const STATUS_COLORS: Record<string, string> = { tracking: "blue", submitted: "orange", won: "green", lost: "red", withdrawn: "default" };

export default function TendersTab({ cid }: { cid: number }) {
  const [msgApi, ctx] = message.useMessage();
  const [form] = Form.useForm();
  const [modal, setModal] = useState<{ open: boolean; rec: any }>({ open: false, rec: null });
  const [page, setPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState<string | undefined>();

  const { data: clients = [] } = useDocClients(cid);
  const { data: tData, isLoading } = useTenders(cid, { page, limit: 50, status: filterStatus });
  const rows = (tData as any)?.data ?? [];
  const total = (tData as any)?.total ?? 0;

  const cb = (msg: string, fn?: () => void) => ({ onSuccess: () => { msgApi.success(msg); fn?.(); }, onError: (e: any) => msgApi.error(e?.message || "Error") });
  const create = useTenderCreate(cid, cb("Tender created.", () => setModal({ open: false, rec: null })));
  const upd = useTenderUpdate(cid, cb("Updated.", () => setModal({ open: false, rec: null })));
  const del = useTenderDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });

  const cols = [
    { title: "Title", dataIndex: "title", key: "t", render: (v: string) => <span className="font-medium text-slate-800 text-xs">{v}</span> },
    { title: "Client", dataIndex: "client_name", key: "cl" },
    { title: "Ref", dataIndex: "reference", key: "r" },
    { title: "Status", dataIndex: "status", key: "st", render: (v: string) => <Tag color={STATUS_COLORS[v] ?? "default"}>{v}</Tag> },
    { title: "Value", dataIndex: "value_estimate", key: "v", render: (v: number) => v ? `£${Number(v).toLocaleString()}` : "—" },
    { title: "Deadline", dataIndex: "submission_deadline", key: "dl", render: (v: string) => { if (!v) return "—"; const past = dayjs(v).isBefore(dayjs()); return <Tag color={past ? "red" : "blue"}>{dayjs(v).format("DD/MM/YY")}</Tag>; } },
    { title: "", key: "act", render: (_: any, r: any) => (
      <Space size="small">
        <Button size="small" onClick={() => { setModal({ open: true, rec: r }); form.setFieldsValue({ ...r, submission_deadline: r.submission_deadline ? dayjs(r.submission_deadline) : null, outcome_date: r.outcome_date ? dayjs(r.outcome_date) : null }); }}>Edit</Button>
        <Popconfirm title="Delete?" onConfirm={() => del.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm>
      </Space>
    )},
  ];

  return (
    <div>
      {ctx}
      <div className="flex justify-between mb-3">
        <Select allowClear placeholder="Filter status" style={{ width: 140 }} onChange={v => { setFilterStatus(v); setPage(1); }} options={["tracking","submitted","won","lost","withdrawn"].map(v => ({ value: v, label: v }))} />
        <Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => { setModal({ open: true, rec: null }); form.resetFields(); form.setFieldsValue({ status: "tracking", created_by: 1 }); }} style={{ background: "#4c1d95" }}>New Tender</Button>
      </div>
      <Table rowKey="id" dataSource={rows} columns={cols} loading={isLoading} size="small" pagination={{ current: page, pageSize: 50, total, onChange: setPage }} />
      <Modal open={modal.open} onCancel={() => setModal({ open: false, rec: null })} footer={null} destroyOnClose title={modal.rec ? "Edit Tender" : "New Tender"} width={520}>
        <Form form={form} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => {
          const p = { ...vals, submission_deadline: vals.submission_deadline?.format("YYYY-MM-DD"), outcome_date: vals.outcome_date?.format("YYYY-MM-DD") };
          if (modal.rec) upd.mutate({ id: modal.rec.id, ...p }); else create.mutate(p);
        }}>
          <Form.Item name="title" label="Tender Title" rules={[{ required: true }]}><Input /></Form.Item>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="client_id" label="Client"><Select allowClear options={(clients as any[]).map((c: any) => ({ value: c.id, label: c.name }))} showSearch optionFilterProp="label" /></Form.Item>
            <Form.Item name="reference" label="Reference"><Input /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="status" label="Status"><Select options={["tracking","submitted","won","lost","withdrawn"].map(v => ({ value: v, label: v }))} /></Form.Item>
            <Form.Item name="value_estimate" label="Est. Value (£)"><InputNumber className="w-full" min={0} /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="submission_deadline" label="Submission Deadline"><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
            <Form.Item name="outcome_date" label="Outcome Date"><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
          </div>
          <Form.Item name="document_url" label="Document URL"><Input /></Form.Item>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={create.isPending || upd.isPending}>Save</Button>
            <Button onClick={() => setModal({ open: false, rec: null })}>Cancel</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
